import React, { useEffect, useMemo } from 'react';
import {convertToCustomElement} from '../formbird/CustomElementWrapper';
import {useFormbird} from '../formbird/FormbirdContext';
import {
    DataGridPremium,
    GRID_TREE_DATA_GROUPING_FIELD,
    GridAlignment,
    GridCellParams,
    GridRowParams,
    GridToolbar,
    GridToolbarContainerProps,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    GridToolbarDensitySelector,
    useGridApiRef,
    GridEventListener,
    GridRenderCellParams,
    GridColDef,
    GridExceljsProcessInput,
} from '@mui/x-data-grid-premium';
import { Box, LinearProgress, Typography } from '@mui/material';
import type {TypographyProps} from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import {configService} from '../services';
import {YTDTrendRenderer} from './utils/Renderers';
import {
    ColoredKPIValueChip,
    formatKpiContactValue,
    formatMonth,
    getPoorRange,
    kpiCellColor,
    MONTHS,
    monthsTill
} from './utils/Misc';
import {KpiContract, Kpis, Row, StringIndexedObject, YTDTrend, Kpi} from './utils/Types';
import {LicenseInfo} from '@mui/x-license-pro';
import {useWatchedFields} from './utils/Hooks';
import type { Column, Cell } from 'exceljs'

const muiXKey = configService.clientConfig<{ muiXKey?: string }>()?.muiXKey;
if (muiXKey) {
    LicenseInfo.setLicenseKey(muiXKey);
}

const raise = (text: string): never => {
    throw Error(text);
};

function processTimeString (value: string) {
    const [minutes, seconds] = value.split(':');
    if (!isNaN(minutes) && !isNaN(seconds)) {
        let serialDate = Number(minutes) * 60 + Number(seconds)
        serialDate = serialDate / (24 * 60 * 60);
        return serialDate;
    }
};

function excelPostProcess({ worksheet }: GridExceljsProcessInput, rows: Row[], monthToDate: string) {
    const YTDColumn = worksheet.getColumn('kpisYTD') as Column;
    if (YTDColumn) {
        YTDColumn.eachCell({ includeEmpty: false }, (cell: Cell, rowNumber) => {
            if (rowNumber !== 1) {
                cell.alignment = { horizontal: 'center' };
                // @ts-expect-error - cell.row is typed incorrectly
                const worksheetRow = worksheet.getRow(cell.row);
                // code is a fixed cell at first position and cannot be hidden
                const code = worksheetRow.getCell(1).value;
                const row = rows.find(it => it.code === code) ?? raise('row exists');

                if (cell.value && !isNaN(cell.value as number)) { 
                    cell.value = Number(cell.value); 
                    const kpiColor = kpiCellColor(row.satisfactoryLimit!, row.unsatisfactoryLimit!, cell.value);
                    let cellColor: string | null = null;
                    switch (kpiColor) {
                        case "green":
                            cellColor = 'ff2e7d32';
                            break;
                        case "red":
                            cellColor = 'ffd32f2f';
                            break;
                        case "yellow":
                            cellColor = 'ffe5e500';
                            break;
                    }
                    if (cellColor !== null) {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: cellColor }
                        }
                        if (kpiColor === 'green' || kpiColor === 'red') {
                            cell.font = {
                                ...cell.font,
                                color: { argb: 'ffffffff' }
                            }
                        }
                    }
                }

                if (cell.value && !isNaN(cell.value as number)) { 
                    const value = Number(cell.value); 
                    cell.value = formatKpiContactValue(value, row.units);
                }
                if (typeof cell.value === 'string') {
                    if ( cell.value.includes(':')){ //   0:00
                        const tmp = processTimeString(cell.value.trim());
                        if (typeof tmp === 'number') {
                            cell.value = tmp;
                            cell.numFmt = '[m]:ss'; // `" "[m]":"s`;
                        }
                    }
                } else { cell.value = 'N/A'; }
            }
        })
    }
    
    const timeValueColumns =  monthsTill(monthToDate);
    for (const timeValueColumn of timeValueColumns) {
        const col = worksheet.getColumn(timeValueColumn) as Column
        col.eachCell({ includeEmpty: false }, (cell: Cell, rowNumber) => {
            if (rowNumber !== 1) {
               
                cell.alignment = { horizontal: 'center' };

                const headerAddress = `${cell.fullAddress.address[0]}1`;
                const headerCell = worksheet.getCell(headerAddress);

                // @ts-expect-error - cell.row is typed incorrectly
                const worksheetRow = worksheet.getRow(cell.row);
                // code is a fixed cell at first position and cannot be hidden
                const code = worksheetRow.getCell(1).value;
                const row = rows.find(it => it.code === code) ?? raise('row exists');
                if (cell.value && !isNaN(cell.value as number)) { 
                    cell.value = Number(cell.value);
                    cell.value = formatKpiContactValue(cell.value, row.units);
                }
                if (typeof cell.value === 'string') {
                    if ( cell.value.includes(':')){ //   0:00
                        const tmp = processTimeString(cell.value.trim());
                        if (typeof tmp === 'number') {
                            cell.value = tmp;
                            cell.numFmt = '[m]:ss'; // `" "[m]":"s`;
                        }
                    }
                }

                const month = MONTHS.find(it => {
                    if (typeof headerCell.value !== 'string') {
                        return false;
                    } else {
                        return headerCell.value?.toLowerCase().startsWith(it);
                    }

                }) ?? raise('month exists');
                const monthValue = row[month] ?? null

                const kpiColor = kpiCellColor(row.satisfactoryLimit!, row.unsatisfactoryLimit!, monthValue);
                let cellColor: string | null = null;
                switch (kpiColor) {
                    case "green":
                        cellColor = 'ff2e7d32';
                        break;
                    case "red":
                        cellColor = 'ffd32f2f';
                        break;
                    case "yellow":
                        cellColor = 'ffe5e500';
                        break;
                }
                if (cellColor !== null) {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: cellColor }
                    }
                    if (kpiColor === 'green' || kpiColor === 'red') {
                        cell.font = {
                            ...cell.font,
                            color: { argb: 'ffffffff' }
                        }
                    }
                }
            }
        })
    }
}

function toTitleCase(str: string) {
  return str.replace(
    /\w\S*/g,
    function(txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    }
  );
}


const ChipTypography = styled(Typography)<TypographyProps>({
    variant: 'body1'
})


const getColumns = (monthToDate: string, addMonths: boolean, addKpisYTD:boolean, fiscalYear:number): GridColDef<Row>[] => {
    const baseRows: GridColDef<Row>[] = [
        {
            field: 'description',
            headerName: 'Description',
            flex: 1
        },
        {
            field: 'units',
            headerName: 'Units'
        },
        {
            field: 'unsatisfactory',
            headerName: 'Unsatisfactory',
            align: 'center'
        },
        {
            field: 'poor',
            headerName: 'Poor',
            align: 'center',
            valueFormatter: params => params.value === null ? 'N/A' : params.value
        },
        {
            field: 'satisfactory',
            headerName: 'Satisfactory',
            align: 'center'
        },
    ];

    if(addKpisYTD){
        const Kpis:GridColDef<Row> =  [{
             field: 'kpisYTD',
             headerName: `YTD ${fiscalYear}`,
             align: 'center',
             valueFormatter: params => params.value === null ? 'N/A' : params.value,
             renderCell: (params: GridRenderCellParams<number | undefined, Row>) => {
                 const row: Row = params.row;
                 if (row.isHeader === false && row.units && (typeof params.value == 'number')) {
                     const color = kpiCellColor(row.satisfactoryLimit!, row.unsatisfactoryLimit!, params.value ?? null) ?? '';
                     const value = formatKpiContactValue(params.value, row.units);
                     return <ColoredKPIValueChip
                         value={value}
                         color={color}
                         sx={{
                             width: '100%',
                             cursor: 'inherit',
                         }}
                         component={ChipTypography}
                     />;
                 } else {
                     return <></>;
                 }
             }
         }]
         baseRows.push(Kpis[0])
     }
    
    if (addMonths) {
        const months = monthsTill(monthToDate).map(month => ({
            field: month,
            headerName: formatMonth(month),
            type: 'string',
            align: 'left' as GridAlignment,
            headerAlign: 'left' as GridAlignment,
            cellClassName: 'clickable-cell',
            valueFormatter: ({ value, id, api }) => {
                if (id === undefined) {
                    throw Error('id is null');
                }
                const row = api.getRow(id);
                return formatKpiContactValue(value, row.units);
            },
            renderCell: (params: GridRenderCellParams<Row>) => {
                const row: Row = params.row;
                if (row.isHeader === false && row.units && (typeof params.value =='number')) { // #19550
                    const color = kpiCellColor(row.satisfactoryLimit!, row.unsatisfactoryLimit!, params.value ?? null) ?? '';
                    const value = formatKpiContactValue(params.value, row.units);
                    return <ColoredKPIValueChip
                        value={value}
                        color={color}
                        sx={{
                            width: '100%',
                            cursor: 'inherit',
                        }}
                        component={ChipTypography}
                    />;
                } else {
                    return <></>;
                }
            }
        } as GridColDef<Row>))
        return [
            ...baseRows,
            {
                field: 'ytdTrend',
                headerName: 'YTD Trend',
                renderCell: YTDTrendRenderer,
                disableExport: true,
                cellClassName: 'clickable-cell'
            },
            ...months
        ]
    } else {
        return [...baseRows]
    }
};


export interface ConfiguredData {
    kpisMonthly?: Kpis[];
    measureSelector: string[];
    contractKpis: KpiContract[];
    monthToDate: string;
    fiscalYear: number;
    kpisYTD?: Kpi[]
}

const CustomToolbar = (props: GridToolbarContainerProps) => (
    <GridToolbarContainer {...props}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
    </GridToolbarContainer>
);

const useConfiguredData = (document: StringIndexedObject, componentDefinition: StringIndexedObject): ConfiguredData => {
    const getFieldName = (fieldName: string, defaultName: string): string => {
        const name = componentDefinition[fieldName] as string;
        return name ?? defaultName;
    };

    const dataField = getFieldName('dataField', 'kpisMonthly');
    const fiscalYearField = getFieldName('fiscalYearField', 'fiscalYear');
    const mtdField = getFieldName('mtdField', 'monthToDate');
    const measureField = getFieldName('measureField', 'measureSelector');
    const kpisField = getFieldName('kpisField', 'contractKpis');
    const dataFieldYTD = getFieldName('dataFieldYTD', 'kpisYTD' )

    const fieldMapping  = {
        [fiscalYearField]: 'fiscalYear',
        [mtdField]: 'monthToDate',
        [measureField]: 'measureSelector',
        [kpisField]: 'contractKpis',
    };

    if (componentDefinition.dataField) {
        fieldMapping[dataField] = 'kpisMonthly'
    }

    if(componentDefinition.dataFieldYTD){
        fieldMapping[dataFieldYTD] = 'kpisYTD'
    }

    const { contractKpis = [], measureSelector = [], monthToDate = 'Jul', kpisMonthly = undefined, fiscalYear = 2022, kpisYTD = undefined } = useWatchedFields<ConfiguredData>(fieldMapping);
    return {
        monthToDate,
        kpisMonthly,
        measureSelector,
        contractKpis,
        fiscalYear,
        kpisYTD
    }
};

const buildRows = (data: ConfiguredData): Row[] => {
    const { contractKpis = [], measureSelector = [], monthToDate = 'Jul' } = data;
    const kpisMonthly: Kpis = {};
    data.kpisMonthly?.forEach((kpi) => {
        const key = Object.keys(kpi)[0];
        kpisMonthly[key] = Object.values(kpi)[0]
    })
    const kpisYTD: Kpi = {};
    data.kpisYTD?.forEach((kpi) => {
        const key = Object.keys(kpi)[0];
        kpisYTD[key] = Object.values(kpi)[0];
    }); 
    const rows: Row[] = contractKpis.map((value) => {
        const unsatisfactoryOp = (value.unsatisfactoryLimit > value.satisfactoryLimit) ? '>=' : '=<';

        let satisfactoryOp;
        if (value.units === 'Percentage' && value.satisfactoryLimit === 100) {
            satisfactoryOp = '=';
        } else if (value.satisfactoryLimit > value.unsatisfactoryLimit) {
            satisfactoryOp = '>=';
        } else {
            satisfactoryOp = '=<';
        }

        const kipMonthly = kpisMonthly[value.code];
        const ytdValue = kpisYTD[value.code];
        const months: StringIndexedObject = {};
        const ytdTrend: YTDTrend = {
            months: [],
            data: [],
        };

        kipMonthly
            ?.slice(0, MONTHS.indexOf(monthToDate.toLowerCase()) + 1)
            ?.forEach((kip, index) => {
                const month = MONTHS[index];
                months[month] = kip;
                ytdTrend.months.push(month);
                ytdTrend.data.push(kip);
            });

        const splitCode = value.code.split('_');
        const lastSplit = splitCode.pop();
        if (lastSplit === undefined) {
            throw Error(`${value.code} does not contain '_'`);
        }
        const treePath = [splitCode.join('.'), lastSplit];

        return {
            ...value,
            code: value.code.replaceAll('_', '.'),
            unsatisfactory: `${unsatisfactoryOp} ${formatKpiContactValue(value.unsatisfactoryLimit, value.units)}`,
            satisfactory: `${satisfactoryOp} ${formatKpiContactValue(value.satisfactoryLimit, value.units)}`,
            poor: getPoorRange(value.satisfactoryLimit, value.unsatisfactoryLimit, value.units),
            isHeader: false,
            treePath,
            ytdTrend,
            kpisYTD: ytdValue,
            ...months,
        };
    });

    if (measureSelector) {
        rows.push(...measureSelector.map(it => {
            const [code, description] = it.split(' - ');
            return {code, description, isHeader: true, treePath: [code]};
        }));
    }
    return rows;
};

const AnnualContractPerformance = () => {
    const { document, componentDefinition, valueChanged } = useFormbird();
    const apiRef = useGridApiRef();
 
    const data = useConfiguredData(document, componentDefinition);
    const rows: Row[] = useMemo(() => buildRows(data), [data]);
    const addMonths = data.kpisMonthly !== undefined;
    const addKpisYTD = data.kpisYTD !== undefined;
    const columns = useMemo(() => getColumns(data?.monthToDate?.toLowerCase(), addMonths, addKpisYTD, data.fiscalYear), [data.monthToDate, addMonths]);

    const onCellClick: GridEventListener<'cellClick'> = (params: GridCellParams<Row>) => {
        console.debug('cell clicked', params)
        const docId = params.row.reportLinkDocId;
        if (params.field === GRID_TREE_DATA_GROUPING_FIELD && !params.row.isHeader) {
            valueChanged([
                docId
            ])
        } else if (MONTHS.includes(params.field)) {
            valueChanged([
                docId,
                params.row.code,
                toTitleCase(params.field),
            ])
        }
    };

    useEffect(() => {
        let listener = () => {
            console.log('resize');
            apiRef.current.resize()
        };
        window.addEventListener('resize', listener)

        return () => window.removeEventListener('resize', listener)
    }, [])


    const getTreeDataPath = (row: Row) => row.treePath;
    const getRowClassName = (params: GridRowParams<Row>) => params.row.isHeader ? 'bold-row' : '';

    return <>
        <Box sx={{
            height: '100%',
            width: '100%',
            '.bold-row': {
                fontWeight: 'bold',
            },
            '.clickable-cell': {
                cursor: 'pointer',
            },
            '.green-cell': {
                backgroundColor: '#00ff00'
            },
            '.red-cell': {
                backgroundColor: '#ff0000'
            },
            '.yellow-cell': {
                backgroundColor: '#ffff00'
            },
            // this is where the virtualScroller is mounted
            '.MuiDataGrid-main > div:nth-child(2)': {
                width: 'auto !important',
            },
            '.MuiDataGrid-virtualScroller': {
                width: 'auto !important',
            },
            '.MuiDataGrid-virtualScrollerContent': {
                width: 'auto !important',
            },
        }}>
            <Typography variant='body1'>{componentDefinition.label}</Typography>
            <DataGridPremium
                autoHeight
                treeData
                disableRowSelectionOnClick
                disableVirtualization
                apiRef={apiRef}
                rows={rows}
                columns={columns}
                getRowId={(r: Row) => r.code}
                defaultGroupingExpansionDepth={1}
                onCellClick={onCellClick}
                groupingColDef={{
                    valueGetter: (r) => r.row.code,
                    headerName: 'Code',
                    filterable: true,
                    sortable: true,
                    hideable: false,
                    width: 150,
                    cellClassName: (params: GridCellParams<Row>) => !params.row.isHeader ? 'clickable-cell' : '',
                }}
                getRowClassName={getRowClassName}
                slots={{
                    toolbar: (addMonths? GridToolbar: CustomToolbar),
                    loadingOverlay: LinearProgress,
                }}
                getTreeDataPath={getTreeDataPath}
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 500 },
                        excelOptions: {
                            exceljsPostProcess: (params) => excelPostProcess(params, rows, data.monthToDate.toLowerCase()),
                        }
                },
                    baseSelect: {
                        native: false,
                    }
                }}
                initialState={{
                    pinnedColumns: { left: [GRID_TREE_DATA_GROUPING_FIELD] }
                }}
            />
        </Box>
    </>;


};

convertToCustomElement('cww-annual-contract-performance', AnnualContractPerformance);
convertToCustomElement('gww-kpi-dashboard', AnnualContractPerformance);
export default AnnualContractPerformance;

